import * as React from 'react';
import { useParams } from 'react-router-dom';
import { Bullseye, Spinner } from '@patternfly/react-core';
import { usePipelineRunV2 } from '~/hooks/usePipelineRunsV2';
import { useTaskRunsForPipelineRuns } from '~/hooks/useTaskRunsV2';
import { useNamespace } from '~/shared/providers/Namespace';
import { getErrorState } from '~/shared/utils/error-utils';
import { useSearchParam } from '../../../../hooks/useSearchParam';
import { RouterParams } from '../../../../routes/utils';
import { PipelineRunLogs } from '../../../../shared';

const PipelineRunLogsTab: React.FC = () => {
  const pipelineRunName = useParams<RouterParams>().pipelineRunName;
  const namespace = useNamespace();
  const [pipelineRun, loaded, error] = usePipelineRunV2(namespace, pipelineRunName);
  const [taskRuns, taskRunsLoaded, taskRunError] = useTaskRunsForPipelineRuns(
    namespace,
    pipelineRunName,
  );
  const [activeTask, setActiveTask, unSetActiveTask] = useSearchParam('task', undefined);
  const [selection, setSelection] = React.useState<{ task: string | undefined }>();

  const handleActiveTaskChange = React.useCallback((value: string | undefined) => {
    setSelection({ task: value });
  }, []);

  // The log viewer selects its first task during mount, before the router can navigate.
  React.useEffect(() => {
    if (selection) {
      selection.task ? setActiveTask(selection.task) : unSetActiveTask();
      setSelection(undefined);
    }
  }, [selection, setActiveTask, unSetActiveTask]);

  if (!(loaded && taskRunsLoaded)) {
    return (
      <Bullseye>
        <Spinner />
      </Bullseye>
    );
  }

  if (error) {
    return getErrorState(error, loaded, 'pipeline run');
  }

  if (taskRunError) {
    return getErrorState(taskRunError, taskRunsLoaded, 'task runs');
  }

  return (
    <PipelineRunLogs
      className="pf-v6-u-pt-md"
      obj={pipelineRun}
      taskRuns={taskRuns}
      activeTask={activeTask}
      onActiveTaskChange={handleActiveTaskChange}
    />
  );
};

export default PipelineRunLogsTab;
